import { createContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import useStudentMonitorEnabled from './useStudentMonitorEnabled'
import useCurrentUserIsStudentSelector from '../../api/selectors/userRoleSelectors/useCurrentUserIsStudentSelector'
import { getSessionAccessToken } from '../../auth/utils'
import {
  ACTION_ALERT_STUDENT_OFFLINE,
  ACTION_ALERT_STUDENT_ONLINE,
  ACTION_LOGIN,
  ACTION_NEW_SCREENSHOT,
  ACTION_TAB_INFO,
} from '@derolfgroep/student-monitor-constants'
import useWebsocketLoggedIn from './useWebsocketLoggedIn'
import useSendExtensionMessage from './useSendExtensionMessage'
import { useSnackbar } from 'notistack'
import currentUserIdSelector from '../../api/selectors/currentUserIdSelector'
import { useDispatch } from 'react-redux'
import { newScreenshotUrl } from '../../pages/GroupV2/redux/screenshotUrlReducer'
import { newTabInfo } from '../../pages/GroupV2/redux/tabInfoReducer'
import { addOnlineStudent, removeOnlineStudent } from '../../pages/GroupV2/redux/onlineStudentReducer'
import { StudentMonitor, WebsocketBrowser } from '@derolfgroep/student-monitor-client'
import * as Sentry from '@sentry/react'
import useSendScreenshots from './useSendScreenshots'
import useShowIndicatorGroup from './useShowIndicatorGroup'

export const remoteViewContext = createContext()

const RemoteViewProvider = ({ extensionIsInstalled, children }) => {
  const isStudent = useCurrentUserIsStudentSelector()
  const isStudentMonitorEnabled = useStudentMonitorEnabled()
  const token = getSessionAccessToken()
  const hasToken = token !== null
  const { setLoggedIn } = useWebsocketLoggedIn()
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const sendExtensionMessage = useSendExtensionMessage()
  const { sendScreenshots } = useSendScreenshots()
  const { showIndicatorGroup } = useShowIndicatorGroup()

  const [studentMonitor, setStudentMonitor] = useState(null)

  /**
   * Websocket should connect if:
   *  - Student monitor is enabled
   *  - User is NOT student OR has extension installed
   *  - Not in Cypress (Can't mock)
   */
  const shouldConnect = useMemo(
    () => Boolean(hasToken && isStudentMonitorEnabled && (!isStudent || extensionIsInstalled) && !window.Cypress),
    [hasToken, isStudentMonitorEnabled, isStudent, extensionIsInstalled]
  )

  useEffect(() => {
    if (!shouldConnect) {
      return () => null
    }

    const client = isStudent ? new StudentMonitor(token).asStudent() : new StudentMonitor(token).asTeacher()

    client.withGateway(new WebsocketBrowser(), process.env.WEBSOCKET_V2_URL)

    if (isStudent) {
      client.gateway.onAll((event) => {
        if (event.args.length <= 0) {
          return
        }

        const message =
          event.event === ACTION_LOGIN
            ? {
                accessToken: token,
                portalUrl: process.env.PORTAL_URL,
                userId: currentUserIdSelector(),
                ...event.args[0],
              }
            : event.args[0]

        sendExtensionMessage(message)
      })
    } else {
      client.withRest(process.env.STUDENT_MONITOR_REST_URL, token)
      client.gateway.on(ACTION_NEW_SCREENSHOT, (message) => {
        dispatch(newScreenshotUrl(message))
      })

      client.gateway.on(ACTION_TAB_INFO, (message) => {
        dispatch(newTabInfo(message))
      })
    }

    client.gateway.on('error', (error) => {
      Sentry.captureEvent(error)
    })

    setStudentMonitor(client)

    client.gateway.on(ACTION_LOGIN, () => {
      setLoggedIn(true)
      // enqueueSnackbar('Verbonden met de Meekijken service', { variant: 'success' })
    })

    client.gateway.on(ACTION_ALERT_STUDENT_ONLINE, (message) => {
      dispatch(addOnlineStudent(message))
    })

    client.gateway.on(ACTION_ALERT_STUDENT_OFFLINE, (message) => {
      dispatch(removeOnlineStudent(message))
    })

    client.gateway.connect()

    return () => {
      client.gateway.close()
      client.gateway.removeAllListeners()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isStudent, shouldConnect, dispatch, enqueueSnackbar, setLoggedIn])

  useEffect(() => {
    const listener = (message) => {
      studentMonitor?.setRemoteViewState(sendScreenshots, sendScreenshots && showIndicatorGroup, [message.studentId])
    }

    if (studentMonitor && studentMonitor.gateway) {
      studentMonitor?.gateway.on(ACTION_ALERT_STUDENT_ONLINE, listener)
    }

    return () => {
      if (studentMonitor && studentMonitor.gateway) {
        studentMonitor.gateway.removeListener(ACTION_ALERT_STUDENT_ONLINE, listener)
      }
    }
  }, [sendScreenshots, showIndicatorGroup, studentMonitor])

  const value = {
    studentMonitor,
  }

  return <remoteViewContext.Provider value={value}>{children}</remoteViewContext.Provider>
}

RemoteViewProvider.propTypes = {
  extensionIsInstalled: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
}

export default RemoteViewProvider
